import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  toast
} from '@sparrowengg/twigs-react';
import { CloseIcon } from '@sparrowengg/twigs-react-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { devPortalServices } from 'commons/services';
import { Spinner } from 'commons/components/spinner';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const getStatusColor = (status: string) => {
  switch ((status || '').toUpperCase()) {
    case 'DONE':
    case 'DEPLOYMENT_COMPLETED':
      return '$positive600';
    case 'OPEN':
    case 'DEPLOYMENT_IN_PROGRESS':
      return '$primary500';
    case 'FAILED':
      return '$negative500';
    case 'CLOSED':
      return '$neutral700';
    default:
      return '$neutral800';
  }
};

const AppReleaseRequests = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appIdFilter = searchParams.get('appId') || '';
  const releaseRequestIdFilter = searchParams.get('releaseRequestId') || '';

  const [requests, setRequests] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [raiseRequestConfirmOpen, setRaiseRequestConfirmOpen] = useState(false);
  const [pendingCloseForRaiseRequest, setPendingCloseForRaiseRequest] =
    useState<any | null>(null);
  const [closeWithMessageOpen, setCloseWithMessageOpen] = useState(false);
  const [requestToClose, setRequestToClose] = useState<any | null>(null);
  const [closeNoteMessage, setCloseNoteMessage] = useState('');
  const [rowActionId, setRowActionId] = useState<number | null>(null);
  const [navigateAfterClose, setNavigateAfterClose] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  const normalizeToArray = (response: any): any[] => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (response?.id || response?.releaseRequestId) return [response];
    return [];
  };

  const getRequestId = (request: any): number => {
    return Number(request?.id ?? request?.releaseRequestId ?? 0);
  };

  const getRequestStatus = (request: any): string => {
    return (
      request?.requestStatus ||
      request?.releaseRequestStatus ||
      request?.status ||
      '-'
    );
  };

  const getAppName = (request: any): string => {
    const properties: any = request?.properties || {};
    const meta = properties['save-app-detail-request'] || {};
    return request?.appName || meta?.appName || request?.name || '-';
  };

  const getMetaData = (request: any) => {
    const properties: any = request?.properties || {};
    return properties['save-app-detail-request'] || {};
  };

  const comments = useMemo(() => {
    if (!selectedRequest) return [];

    const normalize = (item: any) => {
      if (!item) return null;
      if (typeof item === 'string') {
        return { message: item, createdAt: selectedRequest.updatedAt };
      }
      if (typeof item === 'object') {
        const message = item.comment || item.message;
        if (!message) return null;
        return {
          id: item.id,
          message,
          createdAt:
            item.createdAt || item.timestamp || selectedRequest.updatedAt,
          createdBy: item.addedBy || item.createdBy
        };
      }
      return null;
    };

    const topComments = selectedRequest.comments;
    if (Array.isArray(topComments)) {
      return topComments.map(normalize).filter(Boolean) as Array<any>;
    }
    if (topComments && typeof topComments === 'object') {
      if (Array.isArray((topComments as any).commentsList)) {
        const list = (topComments as any).commentsList as Array<any>;
        return list.map(normalize).filter(Boolean) as Array<any>;
      }
      const single = normalize(topComments);
      return single ? [single] : [];
    }

    const propsComments = (selectedRequest.properties as any)?.comments;
    if (Array.isArray(propsComments)) {
      return propsComments.map(normalize).filter(Boolean) as Array<any>;
    }
    if (propsComments && typeof propsComments === 'object') {
      if (Array.isArray((propsComments as any).commentsList)) {
        const list = (propsComments as any).commentsList as Array<any>;
        return list.map(normalize).filter(Boolean) as Array<any>;
      }
      const single = normalize(propsComments);
      return single ? [single] : [];
    }

    return [];
  }, [selectedRequest]);

  const fetchRequests = async () => {
    if (!appIdFilter) {
      setRequests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response =
        await devPortalServices.getAppReleaseRequests(appIdFilter);
      const list = normalizeToArray(response);
      setRequests(list);
      if (selectedRequest) {
        const match = list.find(
          (item: any) => getRequestId(item) === getRequestId(selectedRequest)
        );
        setSelectedRequest(match || null);
      }
    } catch (err: any) {
      console.error(err);
      toast({
        variant: 'error',
        description: err?.message || 'Error in fetching app release requests'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [appIdFilter]);

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      const requestId = String(getRequestId(request));
      const matchRequestId = releaseRequestIdFilter
        ? requestId === releaseRequestIdFilter
        : true;
      return matchRequestId;
    });
  }, [requests, releaseRequestIdFilter]);

  const canCloseRequest = (request: any): boolean => {
    const status = getRequestStatus(request).toUpperCase();
    return status === 'OPEN' || status === 'FAILED';
  };

  const deploymentBlocksNewRequest = useMemo(
    () =>
      requests.some((request) => {
        const status = getRequestStatus(request).toUpperCase();
        return status.startsWith('DEPLOYMENT');
      }),
    [requests]
  );

  const handleConfirmDeleteForRaiseRequest = async () => {
    if (!pendingCloseForRaiseRequest) return;
    const releaseRequestId = getRequestId(pendingCloseForRaiseRequest);
    if (!releaseRequestId) return;

    setRowActionId(releaseRequestId);
    try {
      await devPortalServices.deleteReleaseRequest(
        releaseRequestId,
        'Update New App Zip'
      );
      toast({
        variant: 'success',
        description: 'Request closed successfully'
      });
      setRaiseRequestConfirmOpen(false);
      setPendingCloseForRaiseRequest(null);
      await fetchRequests();
      if (navigateAfterClose && appIdFilter) {
        navigate(`/edit-app/${appIdFilter}?action=raise-release-request`);
      }
    } catch (err: any) {
      console.error(err);
      toast({
        variant: 'error',
        description: err?.message || 'Error in closing the request'
      });
    } finally {
      setRowActionId(null);
      setNavigateAfterClose(false);
    }
  };

  const handleSubmitCloseWithNote = async () => {
    if (!requestToClose) return;
    const releaseRequestId = getRequestId(requestToClose);
    if (!releaseRequestId) return;

    setRowActionId(releaseRequestId);
    try {
      const note = closeNoteMessage.trim() || 'Closed by developer';
      await devPortalServices.deleteReleaseRequest(releaseRequestId, note);
      toast({
        variant: 'success',
        description: 'Request closed successfully'
      });
      setCloseWithMessageOpen(false);
      setRequestToClose(null);
      setCloseNoteMessage('');
      await fetchRequests();
      if (
        selectedRequest &&
        getRequestId(selectedRequest) === releaseRequestId
      ) {
        setSelectedRequest(null);
      }
    } catch (err: any) {
      console.error(err);
      toast({
        variant: 'error',
        description: err?.message || 'Error in closing the request'
      });
    } finally {
      setRowActionId(null);
    }
  };

  const handleRaiseNewRequest = () => {
    if (!appIdFilter) {
      return;
    }
    if (deploymentBlocksNewRequest) {
      toast({
        variant: 'error',
        description:
          'Cannot raise a new release request while a request is in deployment (e.g. deployment started or in progress). Please wait until it finishes.'
      });
      return;
    }
    const actionableRequest = requests.find((request) =>
      canCloseRequest(request)
    );
    if (actionableRequest) {
      setPendingCloseForRaiseRequest(actionableRequest);
      setNavigateAfterClose(true);
      setRaiseRequestConfirmOpen(true);
      return;
    }
    navigate(`/edit-app/${appIdFilter}?action=raise-release-request`);
  };

  const isReleaseRequestModalOpen =
    closeWithMessageOpen || raiseRequestConfirmOpen;

  const handleCommentSubmit = async () => {
    if (!selectedRequest) return;
    if (!commentInput.trim()) {
      toast({
        variant: 'error',
        description: 'Please enter a comment'
      });
      return;
    }
    const releaseRequestId = getRequestId(selectedRequest);
    if (!releaseRequestId) return;

    setCommentLoading(true);
    try {
      await devPortalServices.addAppReleaseRequestCommentById({
        releaseRequestId,
        comment: commentInput.trim()
      });
      toast({
        variant: 'success',
        description: 'Comment added'
      });
      setCommentInput('');
      await fetchRequests();
    } catch (err: any) {
      console.error(err);
      toast({
        variant: 'error',
        description: err?.message || 'Error in adding comment'
      });
    } finally {
      setCommentLoading(false);
    }
  };

  return (
    <Box
      css={{
        width: '100%',
        background: '$white900',
        paddingInline: '$20',
        overflow: 'hidden',
        borderRadius: '$xl',
        height: 'calc(100vh - 60px - 70px)',
        position: 'relative'
      }}
    >
      <Box
        css={{
          height: '100%',
          overflow: 'hidden',
          paddingTop: '$10'
        }}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          css={{
            paddingBlock: '$6',
            borderBottom: '$borderWidths$xs solid $neutral300'
          }}
        >
          <Heading size="h4">App Release Requests</Heading>
          <Flex alignItems="center" gap="$4">
            <Button
              size="md"
              color="secondary"
              onClick={handleRaiseNewRequest}
              disabled={!appIdFilter || deploymentBlocksNewRequest}
            >
              Raise New Request
            </Button>
            <Button size="md" variant="outline" onClick={fetchRequests}>
              Refresh
            </Button>
          </Flex>
        </Flex>

        {!appIdFilter ? (
          <Flex
            alignItems="center"
            justifyContent="center"
            css={{ height: '70vh' }}
          >
            <Text size="md" css={{ color: '$neutral700' }}>
              Missing app id. Open this page from an app card.
            </Text>
          </Flex>
        ) : loading ? (
          <Flex
            alignItems="center"
            justifyContent="center"
            css={{ height: '70vh' }}
          >
            <Spinner size="md" />
          </Flex>
        ) : (
          <Box
            css={{
              marginTop: '$10',
              border: '$borderWidths$xs solid $neutral200',
              borderRadius: '$md',
              overflow: 'auto',
              height: 'calc(100% - 70px)'
            }}
          >
            <Table
              css={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse'
              }}
            >
              <colgroup>
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <Thead
                css={{
                  '& th': {
                    background: '$neutral100',
                    borderBottom: '$borderWidths$xs solid $neutral200',
                    padding: '$8 $10',
                    color: '$neutral800',
                    fontSize: '$sm',
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    fontWeight: '$8',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }
                }}
              >
                <Th>Release Request Id</Th>
                <Th>App Id</Th>
                <Th>App Name</Th>
                <Th>Status</Th>
                <Th>Created At</Th>
                <Th></Th>
              </Thead>
              <Tbody>
                {filteredRequests.map((request) => {
                  const requestId = getRequestId(request);
                  const status = getRequestStatus(request);
                  return (
                    <Tr
                      key={requestId}
                      css={{
                        cursor: 'pointer',
                        '& td': {
                          padding: '$12 $12',
                          paddingRight: '$14',
                          borderBottom: '$borderWidths$xs solid $neutral200',
                          verticalAlign: 'middle'
                        },
                        backgroundColor:
                          getRequestId(selectedRequest) === requestId
                            ? '$neutral50'
                            : '$white900'
                      }}
                      onClick={() => {
                        setSelectedRequest(request);
                        setCommentInput('');
                      }}
                    >
                      <Td>{requestId || '-'}</Td>
                      <Td>{request?.appId || appIdFilter}</Td>
                      <Td>{getAppName(request)}</Td>
                      <Td>
                        <Box
                          css={{
                            display: 'inline-block',
                            padding: '$2 $4',
                            borderRadius: '$full',
                            background: '$neutral100',
                            color: getStatusColor(status),
                            fontWeight: 600,
                            fontSize: '$sm'
                          }}
                        >
                          {status}
                        </Box>
                      </Td>
                      <Td>
                        {formatDate(request?.created_at || request?.createdAt)}
                      </Td>
                      <Td>
                        <Flex justifyContent="flex-end">
                          {canCloseRequest(request) ? (
                            <Button
                              size="md"
                              color="default"
                              variant="ghost"
                              onClick={(event) => {
                                event.stopPropagation();
                                setRequestToClose(request);
                                setCloseNoteMessage('');
                                setCloseWithMessageOpen(true);
                              }}
                            >
                              Close
                            </Button>
                          ) : null}
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
                {!filteredRequests.length ? (
                  <Tr>
                    <Td colSpan={6}>
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        css={{ padding: '$12' }}
                      >
                        <Text
                          size="md"
                          weight="medium"
                          css={{ color: '$black600' }}
                        >
                          No release requests found
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : null}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {selectedRequest && !isReleaseRequestModalOpen ? (
        <Box
          css={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 999999
          }}
          onClick={() => setSelectedRequest(null)}
        >
          <Box
            css={{
              width: 520,
              maxWidth: '90%',
              height: '100vh',
              background: '$white900',
              borderLeft: '$borderWidths$xs solid $neutral200',
              boxShadow: '$3xl',
              padding: '$10',
              overflow: 'hidden'
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              css={{
                borderBottom: '$borderWidths$xs solid $neutral300',
                paddingBottom: '$4'
              }}
            >
              <Heading size="h5">Request Details</Heading>
              <IconButton
                size="xl"
                variant="ghost"
                color="default"
                icon={<CloseIcon size={52} />}
                onClick={() => setSelectedRequest(null)}
                aria-label="Close details"
              />
            </Flex>

            <Flex
              flexDirection="column"
              gap="$8"
              css={{ marginTop: '$8', height: 'calc(100% - 60px)' }}
            >
              <Box
                css={{
                  padding: '$8',
                  border: '$borderWidths$xs solid $neutral200',
                  borderRadius: '$md',
                  background: '$neutral50'
                }}
              >
                <Flex justifyContent="space-between">
                  <Box>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      Release Request Id
                    </Text>
                    <Text size="lg" weight="medium">
                      {getRequestId(selectedRequest)}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      Status
                    </Text>
                    <Text
                      weight="medium"
                      css={{
                        color: getStatusColor(getRequestStatus(selectedRequest))
                      }}
                    >
                      {getRequestStatus(selectedRequest)}
                    </Text>
                  </Box>
                </Flex>
                <Flex gap="$10" css={{ marginTop: '$6' }}>
                  <Box>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      App Id
                    </Text>
                    <Text weight="medium">
                      {selectedRequest?.appId || appIdFilter}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      App Name
                    </Text>
                    <Text weight="medium">{getAppName(selectedRequest)}</Text>
                  </Box>
                </Flex>
                <Flex gap="$10" css={{ marginTop: '$4' }}>
                  <Box>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      Created
                    </Text>
                    <Text>
                      {formatDate(
                        selectedRequest?.created_at ||
                          selectedRequest?.createdAt
                      )}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      Updated
                    </Text>
                    <Text>{formatDate(selectedRequest?.updatedAt)}</Text>
                  </Box>
                </Flex>
                {getMetaData(selectedRequest)?.shortDescription ? (
                  <Box css={{ marginTop: '$6' }}>
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      Overview
                    </Text>
                    <Text css={{ color: '$neutral700', marginTop: '$2' }}>
                      {getMetaData(selectedRequest).shortDescription}
                    </Text>
                  </Box>
                ) : null}
              </Box>

              <Box
                css={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '$borderWidths$xs solid $neutral200',
                  borderRadius: '$md',
                  overflow: 'hidden'
                }}
              >
                <Box
                  css={{
                    padding: '$6',
                    borderBottom: '$borderWidths$xs solid $neutral200',
                    background: '$neutral100'
                  }}
                >
                  <Heading size="h6">Comments</Heading>
                </Box>
                <Box
                  css={{
                    padding: '$6',
                    flex: 1,
                    overflowY: 'auto'
                  }}
                >
                  {comments.length ? (
                    <Flex flexDirection="column" gap="$6">
                      {comments.map((comment: any, index: number) => (
                        <Box
                          key={`${comment.id || index}-${
                            comment.createdAt || ''
                          }`}
                          css={{
                            padding: '$4',
                            borderRadius: '$md',
                            background: '$white900',
                            border: '$borderWidths$xs solid $neutral200'
                          }}
                        >
                          <Text css={{ color: '$neutral800' }}>
                            {comment.message}
                          </Text>
                          <Text
                            size="xs"
                            css={{ color: '$neutral600', marginTop: '$2' }}
                          >
                            {comment.createdBy ? `${comment.createdBy} · ` : ''}
                            {formatDate(comment.createdAt)}
                          </Text>
                        </Box>
                      ))}
                    </Flex>
                  ) : (
                    <Text size="sm" css={{ color: '$neutral700' }}>
                      No comments yet
                    </Text>
                  )}
                </Box>
                <Box
                  css={{
                    padding: '$6',
                    borderTop: '$borderWidths$xs solid $neutral200',
                    background: '$neutral50'
                  }}
                >
                  <Textarea
                    value={commentInput}
                    onChange={(event) =>
                      setCommentInput(event.currentTarget.value)
                    }
                    placeholder="Add a comment"
                    rows={3}
                  />
                  <Flex
                    justifyContent="flex-end"
                    gap="$4"
                    css={{ marginTop: '$4' }}
                  >
                    <Button
                      size="sm"
                      color="secondary"
                      loading={commentLoading}
                      onClick={handleCommentSubmit}
                    >
                      Send
                    </Button>
                  </Flex>
                </Box>
              </Box>
            </Flex>
          </Box>
        </Box>
      ) : null}

      {closeWithMessageOpen && requestToClose ? (
        <CloseReleaseRequestDialog
          resetModal={() => {
            setCloseWithMessageOpen(false);
            setRequestToClose(null);
            setCloseNoteMessage('');
          }}
          note={closeNoteMessage}
          onNoteChange={setCloseNoteMessage}
          btnLoader={rowActionId === getRequestId(requestToClose)}
          onConfirm={handleSubmitCloseWithNote}
          releaseRequestId={getRequestId(requestToClose)}
          appName={getAppName(requestToClose)}
        />
      ) : null}

      {raiseRequestConfirmOpen && (
        <ReleaseRequestConfirmModal
          resetModal={() => {
            setNavigateAfterClose(false);
            setRaiseRequestConfirmOpen(false);
            setPendingCloseForRaiseRequest(null);
          }}
          btnLoader={
            pendingCloseForRaiseRequest
              ? rowActionId === getRequestId(pendingCloseForRaiseRequest)
              : false
          }
          onClickHandler={handleConfirmDeleteForRaiseRequest}
          appName={
            getAppName(pendingCloseForRaiseRequest) ||
            searchParams.get('appName') ||
            ''
          }
        />
      )}
    </Box>
  );
};

const CloseReleaseRequestDialog = ({
  resetModal,
  note,
  onNoteChange,
  btnLoader,
  onConfirm,
  releaseRequestId,
  appName
}: {
  resetModal: () => void;
  note: string;
  onNoteChange: (value: string) => void;
  btnLoader?: boolean;
  onConfirm: () => void;
  releaseRequestId: number;
  appName: string;
}) => {
  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 476,
          zIndex: 10000000
        }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Close release request</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
            aria-label="Close dialog"
          />
        </Flex>
        <Flex flexDirection="column" gap="$6" css={{ padding: '$12' }}>
          <Text size="sm" css={{ color: '$neutral700' }}>
            Request #{releaseRequestId}
            {appName && appName !== '-' ? ` · ${appName}` : ''}
          </Text>
          <Flex flexDirection="column" gap="$2">
            <FormLabel>Message (optional)</FormLabel>
            <Textarea
              value={note}
              onChange={(event) => onNoteChange(event.currentTarget.value)}
              placeholder="Add a note about why you are closing this request"
              rows={4}
            />
          </Flex>
          <Text size="sm" css={{ color: '$neutral700' }}>
            This will delete the release request. You can start a new one later
            from the app card if needed.
          </Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="default"
            variant="outline"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onConfirm()}
            disabled={btnLoader}
            loading={btnLoader}
          >
            Close request
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

const ReleaseRequestConfirmModal = ({
  resetModal,
  btnLoader,
  onClickHandler,
  appName
}: {
  resetModal: () => void;
  btnLoader?: boolean;
  onClickHandler: () => void;
  appName: string;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);

  return (
    <Dialog open>
      <DialogContent
        css={{
          padding: 0,
          borderRadius: '$xl',
          width: 476,
          zIndex: 10000000
        }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Delete Release Request</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
          />
        </Flex>
        <Flex flexDirection="column" gap="$14" css={{ padding: '$12' }}>
          <Flex flexDirection="column" gap="$4">
            <Text size="sm">
              To delete the release request, type the app name{' '}
              <Text
                as="span"
                size="sm"
                css={{ color: '$secondary400', whiteSpace: 'break-spaces' }}
                weight="bold"
              >
                {appName}
              </Text>
            </Text>
            <Input
              size="lg"
              onChange={(e) => {
                e.preventDefault();
                const inputValue = e?.currentTarget?.value?.trim() || '';
                const appNameNormalized = appName?.trim() || '';
                if (inputValue === appNameNormalized) {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            />
            <FormLabel>The app name must be case-sensitive</FormLabel>
          </Flex>
          <Text size="md">
            <Box as="strong">Note:</Box> This app has a pending release request.
            To make changes, the existing request needs to be deleted first.
          </Text>
          <Text size="md">
            Are you sure you want to delete the release request?
          </Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="error"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            No, Go Back
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onClickHandler()}
            disabled={isDisabled || btnLoader}
            loading={btnLoader}
          >
            Confirm
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default AppReleaseRequests;
